import type { FC, ReactNode } from 'react';
import { mergeClasses } from '@fluentui/react-components';
import { useSectionIconStyles } from './SectionIcon.styles';

export interface SectionIconProps {
  /** Icon element to render inside the tinted square (usually a Fluent UI icon). */
  icon: ReactNode;
  /**
   * Optional accessible label for the icon chip. When provided, the chip gets
   * `role="img"` and `aria-label`; otherwise it is marked `aria-hidden` so
   * the visible label next to it remains the sole accessible name.
   */
  ariaLabel?: string;
  /** Additional class name to merge with the chip root. */
  className?: string;
}

/**
 * Decorative 36×36 tinted-square icon chip used by Figma-aligned section
 * headings on Employee screens. Purely presentational: no behavioural props,
 * no state, no event handlers.
 *
 * Reference: companion plan §3.4 Iconography.
 */
export const SectionIcon: FC<SectionIconProps> = ({ icon, ariaLabel, className }) => {
  const styles = useSectionIconStyles();
  const accessibility = ariaLabel
    ? ({ role: 'img', 'aria-label': ariaLabel } as const)
    : ({ 'aria-hidden': true } as const);

  return (
    <span className={mergeClasses(styles.root, className)} {...accessibility}>
      <span className={styles.icon} aria-hidden>
        {icon}
      </span>
    </span>
  );
};

SectionIcon.displayName = 'SectionIcon';
